import API from "./axios"
import { getUserInfo } from "../utils/authDecode"

export async function getPublicRooms(tenantId, branchId = 1) {
  const { data } = await API.get(
    `v1/reservation/tenants/${tenantId}/branches/${branchId}/rooms`,
  )
  return data.data
}

export async function createGuestBooking(payload) {
  const { data } = await API.post("v1/reservation/bookings/guest", payload)
  const otpState = data.data?.next_steps?.otp_state || {}

  return {
    ...(data.data || {}),
    success: data.success,
    message: data.message,
    code: data.code ?? otpState.code,
    otp_sent: data.otp_sent ?? otpState.otp_sent,
    already_active: data.already_active ?? otpState.already_active,
    attempts_remaining: data.attempts_remaining ?? otpState.attempts_remaining,
    max_attempts: data.max_attempts ?? otpState.max_attempts,
    expires_in: data.expires_in ?? otpState.expires_in,
    cooldown_remaining: data.cooldown_remaining ?? otpState.cooldown_remaining,
    lockout_remaining: data.lockout_remaining ?? otpState.lockout_remaining,
  }
}

export async function verifyGuestBookingOtp(bookingId, otp) {
  const { data } = await API.post("v1/reservation/bookings/guest/verify-otp", {
    booking_id: bookingId,
    otp_code: otp,
  })

  return data.data
}

export async function getGuestBookingStatus() {
  const bookingId = sessionStorage.getItem("bookingId")
  const { data } = await API.get(
    `v1/reservation/bookings/guest/status/${bookingId}`,
  )

  return data.data
}

export async function createRegisteredUserBooking(payload) {
  const { data } = await API.post("v1/reservation/bookings/registered", payload)
  return data
}

export async function getRegisteredUserBookingStatus() {
  const bookingId = sessionStorage.getItem("bookingId")
  const { data } = await API.get(
    `v1/reservation/bookings/registered/status/${bookingId}`,
  )

  return data.data
}

export async function getUnifiedBookingData(branchId) {
  const userInfo = getUserInfo()
  const tenantId = sessionStorage.getItem("tenantId")
  const isSuperAdmin = Array.isArray(userInfo?.role)
    ? userInfo.role.some((role) => String(role).toLowerCase() === "superadmin")
    : String(userInfo?.role || "").toLowerCase() === "superadmin"
  const isAdminManager = Array.isArray(userInfo?.role)
    ? userInfo.role.some(
        (role) => String(role).toLowerCase() === "admin_manager",
      ) // admin_manager is not the final role name for Branch Admin, will change this in future enhancements
    : String(userInfo?.role || "").toLowerCase() === "admin_manager"
  const isTenantAdmin = Array.isArray(userInfo?.role)
    ? userInfo.role.some(
        (role) => String(role).toLowerCase() === "tenant_admin",
      )
    : String(userInfo?.role || "").toLowerCase() === "tenant_admin"
  const rawBranchId =
    branchId ?? userInfo?.branch_id ?? sessionStorage.getItem("branchId")
  const resolvedBranchId = Array.isArray(rawBranchId)
    ? rawBranchId.find((id) => id !== undefined && id !== null && id !== "")
    : rawBranchId
  const params = {}

  if (
    !isSuperAdmin &&
    tenantId !== undefined &&
    tenantId !== null &&
    tenantId !== ""
  ) {
    params.tenant_id = tenantId
  }

  if (
    !isSuperAdmin &&
    (isAdminManager || !isTenantAdmin) &&
    resolvedBranchId !== undefined &&
    resolvedBranchId !== null &&
    resolvedBranchId !== ""
  ) {
    params.branch_id = resolvedBranchId
  }

  const { data } = await API.get("v1/reservation/bookings", {
    params,
  })
  return data.data
}

export async function getSpecificBranchByTenant(tenantId, branchId) {
  const query = new URLSearchParams()
  if (branchId !== undefined && branchId !== null && branchId !== "") {
    query.append("branchId", branchId)
  }

  const queryString = query.toString()
  const url = queryString
    ? `v1/public/tenants/${tenantId}/branches?${queryString}`
    : `v1/public/tenants/${tenantId}/branches`

  const { data } = await API.get(url)
  return data.data
}

export async function getAllBranchesByTenant(tenantId) {
  const { data } = await API.get(`v1/public/tenants/${tenantId}/branches`)
  return data.data
}

export async function updateBookingStatus(payload) {
  const userInfo = getUserInfo()
  const tenantId = sessionStorage.getItem("tenantId")
  const payloadBranchId = payload?.branch_id
  const resolvedPayloadBranchId = Array.isArray(payloadBranchId)
    ? payloadBranchId.find((id) => id !== undefined && id !== null && id !== "")
    : payloadBranchId
  const branchId =
    resolvedPayloadBranchId ||
    userInfo?.branch_id ||
    sessionStorage.getItem("branchId")
  const requestPayload =
    payload && Object.prototype.hasOwnProperty.call(payload, "branch_id")
      ? { ...payload, branch_id: undefined }
      : payload
  const { data } = await API.patch(
    `v1/reservation/branch/${tenantId}/${branchId}/bookings/status`,
    requestPayload,
  )

  return data.data

  //superadmin: send neither tenant_id nor branch_id
  //admin_manager: send both tenant_id and branch_id (when IDs exist)
  //tenant_admin: send tenant_id only
}

export async function updateBookingDetails(payload, bookingId) {
  const userInfo = getUserInfo()
  const tenantId = sessionStorage.getItem("tenantId")
  const branchId = userInfo?.branch_id || sessionStorage.getItem("branchId")
  const { data } = await API.patch(
    `v1/reservation/branch/${tenantId}/${branchId}/bookings/${bookingId}`,
    payload,
  )

  return data.data
}

export async function getBlockedDateByTenant(
  tenantId,
  branchId,
  roomId,
  { startDate, endDate },
) {
  const params = new URLSearchParams({
    startDate,
    endDate,
  })

  const { data } = await API.get(
    `v1/reservation/tenants/${tenantId}/branches/${branchId}/rooms/${roomId}/blocked-slots?${params.toString()}`,
  )

  return data.data
}

export async function getActiveRoomsByTenantAndBranch(tenantId, branchId) {
  const { data } = await API.get(
    `v1/reservation/tenants/${tenantId}/branches/${branchId}/rooms`,
  )

  return data.data
}

export async function createBranch(branchData) {
  try {
    const headers = {}

    if (branchData.tenant_id) {
      headers["X-Tenant-ID"] = branchData.tenant_id
    }

    const { data } = await API.post("/v1/branches", branchData, {
      headers,
    })
    return data
  } catch (error) {
    console.error("Error creating branch:", error)
    throw error
  }
}

export async function createRooms(branchId, rooms, tenantId = null) {
  try {
    const results = []

    for (let i = 0; i < rooms.length; i++) {
      const room = rooms[i]
      const formData = new FormData()
      const headers = {
        "Content-Type": "multipart/form-data",
      }

      formData.append("name", room.name)
      formData.append("capacity", room.capacity.toString())
      formData.append("rate", room.rate.toString())
      formData.append("is_available", room.is_available.toString())

      if (tenantId) {
        formData.append("tenant_id", tenantId)
        headers["X-Tenant-ID"] = tenantId
      }

      try {
        const { data } = await API.post(
          `/v1/reservation/branch/${branchId}/rooms`,
          formData,
          {
            headers,
          },
        )
        results.push(data)
      } catch (roomError) {
        throw roomError
      }
    }

    return { success: true, results }
  } catch (error) {
    console.error("Error creating rooms:", error)
    throw error
  }
}

export async function uploadVenueImages(tenantId, branchId, venueImages) {
  try {
    const validImages = (venueImages || [])
      .filter((imageData) => imageData && imageData.file)
      .map((imageData) => imageData.file)

    if (validImages.length === 0) {
      return { success: true, message: "No images to upload" }
    }

    const formData = new FormData()

    validImages.forEach((file) => {
      formData.append("images", file)
    })

    const { data } = await API.post("/v1/tenants/gallery/images", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Tenant-ID": tenantId,
        "X-Branch-ID": branchId,
      },
    })

    return { success: true, data }
  } catch (error) {
    console.error("Error uploading venue images:", error)
    throw error
  }
}

export async function uploadFloorPlan(tenantId, branchId, floorPlan) {
  try {
    if (!floorPlan?.file) {
      return {
        success: true,
        message: "No floor plan to upload"
      };
    }

    const formData = new FormData();
    formData.append("floor_plan", floorPlan.file);

    const { data } = await API.post(`/v1/branches/${branchId}/floor-plan`, formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Tenant-ID": tenantId,
          "X-Branch-ID": branchId
        }
      }
    );

    return data;
  } catch (error) {
    console.error("Error uploading floor plan:", error);
    throw error;
  }
}

export async function deleteFloorPlan(tenantId, branchId) {
  try {
    const { data } = await API.delete(
      `/v1/branches/${branchId}/floor-plan`,
      {
        headers: {
          "X-Tenant-ID": tenantId,
          "X-Branch-ID": branchId,
        },
      }
    )

    return { success: true, data }
  } catch (error) {
    console.error("Error deleting floor plan:", error)
    throw error
  }
}

export async function deleteVenueImage(imageId, tenantId, branchId) {
  try {
    const { data } = await API.delete(`/v1/tenants/gallery/images/${imageId}`, {
      headers: {
        "X-Tenant-ID": tenantId,
        "X-Branch-ID": branchId,
      },
    })
    return { success: true, data }
  } catch (error) {
    console.error("Error deleting venue image:", error)
    throw error
  }
}

export async function bulkDeleteVenueImages(imageIds, tenantId, branchId) {
  try {
    if (!imageIds || imageIds.length === 0) {
      return { success: true, message: "No images to delete" }
    }

    const { data } = await API.post(
      "/v1/tenants/gallery/images/bulk-delete",
      { imageIds },
      {
        headers: {
          "X-Tenant-ID": tenantId,
          "X-Branch-ID": branchId,
        },
      },
    )
    return { success: true, data }
  } catch (error) {
    console.error("Error bulk deleting venue images:", error)
    throw error
  }
}

export async function deleteRoom(branchId, roomId, tenantId = null) {
  try {
    const headers = {"Content-Type": "multipart/form-data"}

    if (tenantId) {
      headers["X-Tenant-ID"] = tenantId
    }

    const { data } = await API.delete(
      `/v1/reservation/branch/${branchId}/rooms/${roomId}`,
      { headers }
    )
    return { success: true, data }
  } catch (error) {
    console.error("Error deleting room:", error)
    throw error
  }
}

export async function updateRoom(branchId, roomId, roomData, tenantId = null) {
  try {
    const formData = new FormData()
    const headers = {"Content-Type": "multipart/form-data",}

    if (roomData.name) formData.append("name", roomData.name)
    if (roomData.capacity)
      formData.append("capacity", roomData.capacity.toString())
    if (roomData.rate) formData.append("rate", roomData.rate.toString())
    if (roomData.is_available !== undefined) {
      formData.append("is_available", roomData.is_available.toString())
    }
    if (tenantId) {
      formData.append("tenant_id", tenantId)
      headers["X-Tenant-ID"] = tenantId
    }

    const { data } = await API.put(
      `/v1/reservation/branch/${branchId}/rooms/${roomId}`,
      formData,
      {
        headers,
      },
    )
    return { success: true, data }
  } catch (error) {
    console.error("Error updating room:", error)
    throw error
  }
}

export async function updateFoodPackage(
  tenantId,
  branchId,
  packageId,
  packageData,
) {
  try {
    const formData = new FormData()

    if (packageData.name) {
      formData.append("name", packageData.name)
    }
    if (packageData.image instanceof File) {
      formData.append("image", packageData.image)
    }

    const { data } = await API.put(
      `/v1/tenants/${tenantId}/branches/${branchId}/food-packages/${packageId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    )
    return { success: true, data }
  } catch (error) {
    console.error("Error updating food package:", error)
    throw error
  }
}

export async function deleteFoodPackage(tenantId, branchId, packageId) {
  try {
    const { data } = await API.delete(
      `/v1/tenants/${tenantId}/branches/${branchId}/food-packages/${packageId}`,
    )
    return { success: true, data }
  } catch (error) {
    console.error("Error deleting food package:", error)
    throw error
  }
}

export async function createFoodPackages(
  tenantId,
  branchId,
  foodPackageNames,
  foodPackageFiles,
) {
  try {
    const token = sessionStorage.getItem("token")
    const validNames = (foodPackageNames || []).filter(
      (name) => name && name.trim(),
    )
    const validFiles = (foodPackageFiles || []).filter(
      (file) => file instanceof File,
    )

    if (validNames.length === 0 && validFiles.length === 0) {
      return { success: true, message: "No food packages to create" }
    }

    const results = []
    const maxPackages = Math.max(validNames.length, validFiles.length)

    for (let i = 0; i < maxPackages; i++) {
      const packageName = validNames[i] || `Food Package ${i + 1}`
      const packageFile = validFiles[i]

      if (!packageName.trim() && !packageFile) continue

      try {
        const formData = new FormData()
        formData.append("name", packageName.trim())

        if (packageFile) {
          formData.append("image", packageFile)
        }

        const { data } = await API.post(
          `/v1/tenants/${tenantId}/branches/${branchId}/food-packages`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          },
        )

        results.push(data)
      } catch (packageError) {
        console.error(
          `Failed to create food package "${packageName}":`,
          packageError.response?.data,
        )
        throw packageError
      }
    }

    return {
      success: true,
      data: results,
      message: `Successfully created ${results.length} food packages`,
    }
  } catch (error) {
    console.error("Error creating food packages:", error)
    throw error
  }
}
